import { html } from "lit-element";
import { getCanvasA4Size } from "../helpers.js";
import { DrawSamplePumpModule } from "./samples-pumps/sample-pump-module.js";
import { DrawPhotoNumber } from "./photo-numbers/photo-number-module.js";
import { StatefulModule } from "../ModuleBase/ModuleMetadata.js";

const lineHeight = 25;

export class DrawLegendModule extends StatefulModule {
	render() {
		return html`
            <style>
                mwc-button {
                    --mdc-theme-on-primary: var(--primary-color);
                    --mdc-theme-primary: var(--primary-color-variant1);
                    --mdc-theme-on-secondary: var(--primary-color);
                    --mdc-theme-secondary: var(--primary-color-variant1);
                }

                *[active] {
                background-color: var(--secondary-color);
                }
            </style>
    	    <mwc-button ?active="${this.isLegendVisible}" @click="${e => this.toggleLegend(e)}">Legend</mwc-button>`;
	}

	static get properties() {
		return {
			isLegendVisible: { type: Boolean },
			isValid: { type: Boolean },
		};
	}

	constructor(canvas) {
		super();
		this.neverSelect = true;
		this.canvas = canvas;
		this.isLegendVisible = true;
		this.moduleName = "legend";
		this.canvasSize = getCanvasA4Size();
		this.legendTop = this.canvasSize.h - 253;
	}

	toggleLegend() {
		this.isLegendVisible = !this.isLegendVisible;
		this.isLegendVisible ? this.showLegend() : this.hideLegend();
	}

	showLegend() {
		const offsetLeft = 20;
		const lineWidth = 47;
		let line = new fabric.Line([0, 0, this.canvasSize.w, 0], { stroke: "black" });

		const labels = [
			"Containment / inspectiegebied",
			"Pompen",
			"Kleefmonsters",
			"Grondmonsters",
			"Fotonummer en -richting",
			"Foliewanden en/of afgeplakte inboedel/materialen",
			"Verpakt afval/- opslag",
			"Transitroute",
			"Afzetlint"
		];

		let texts = labels.map((l, i) =>
			new fabric.Text(l, {
				fontSize: 15,
				fontFamily: "Arial",
				left: 75,
				top: lineHeight * (i + 1),
				originY: "center"
			})
		);
		
		let anglePromise = DrawPhotoNumber.MakeAngle({ x: 33, y: lineHeight * 5 }, "e");
		anglePromise.then(angle => {
			let shapes = [
				new fabric.Rect({
					left: offsetLeft,
					top: lineHeight,
					width: lineWidth,
					height: 15,
					fill: "gray",
					originY: "center"
				}),
				DrawSamplePumpModule.MakeGroup(
					{ x: 43, y: lineHeight * 2 - 11 },
					"X",
					"cyan",
					"red",
					10
				),
				DrawSamplePumpModule.MakeGroup(
					{ x: 43, y: lineHeight * 3  - 11 },
					"X",
					"lime",
					"red",
					10
				),
				DrawSamplePumpModule.MakeGroup(
					{ x: 43, y: lineHeight * 4  - 11 },
					"X",
					"magenta",
					"blue",
					10
				),
				angle,
				new fabric.Rect({
					left: offsetLeft,
					top: lineHeight * 6,
					width: lineWidth,
					height: 15,
					fill: "blue",
					originY: "center"
				}),
				new fabric.Rect({
					left: offsetLeft,
					top: lineHeight * 7,
					width: lineWidth,
					height: 15,
					fill: "yellow",
					originY: "center"
				}),
				new fabric.Line(
					[offsetLeft, lineHeight * 8, offsetLeft + lineWidth, lineHeight * 8],
					{
						strokeDashArray: [5, 2],
						stroke: "blue"
					}
				),
				new fabric.Line(
					[offsetLeft, lineHeight * 9, offsetLeft + lineWidth, lineHeight * 9],
					{
						strokeDashArray: [2, 2],
						stroke: "orange",
						strokeWidth: 2
					}
				)
			];

			let inspectionInfo = [];
			if (INSP.APP.state.inspection.current) {
				const curInsp = INSP.APP.state.inspection.current
				if (curInsp.Inspectie_info && curInsp.Inspectie_info.inspectieInfo &&
                    curInsp.Inspectie_info.inspectieInfo.rPSProjectnummer) {
					inspectionInfo.push(
						new fabric.Text("Projectnummer: " + curInsp.Inspectie_info.inspectieInfo.rPSProjectnummer, {
							fontSize: 15,
							fontFamily: "Arial",
							left: this.canvasSize.w - offsetLeft,
							top: lineHeight,
							originX: 'right',
							originY: "center"
						})
					)
				}
				if (curInsp.inspectieDetails && curInsp.inspectieDetails.Inspectie_details) {
					if (curInsp.inspectieDetails.Inspectie_details.rPSMonsternummer) {
						inspectionInfo.push(
							new fabric.Text("Monsternummer: " + curInsp.inspectieDetails.Inspectie_details.rPSMonsternummer, {
								fontSize: 15,
								fontFamily: "Arial",
								left: this.canvasSize.w - offsetLeft,
								top: lineHeight * 2,
								originX: 'right',
								originY: "center"
							})
						);
					}					
				}
			}

			let group = new fabric.Group([line, ...texts, ...shapes, ...inspectionInfo], {
				top: this.legendTop,
				left: 0,
				hasBorders: true,
				hasControls: false,
				selectable: false
			});

			this.addMetadata(group, { neverSelect: true, settingsSelect: true, extraLines: 0 });

			this.canvas.add(group);
			this.canvas.renderAll();
		});

		return anglePromise;
	}

	updateState() {
		let found = false;
		this.canvas.forEachObject(o => {
			found = found || (o.moduleName && o.moduleName === this.constructor.name);
		});
		this.isLegendVisible = found;
	}

	hideLegend() {
		this.canvas.forEachObject(o => {
			if (o.moduleName && o.moduleName === this.constructor.name) {
				this.canvas.remove(o);
			}
		});
	}

	RenderSettings(obj, closeFunc) {
		const currentSettings = obj.moduleState;
		let tempExtraLines = currentSettings.extraLines;
		let isValid = true;

		const applyFunc = () => {
			if (isValid) {
				this.applyNewSettings(obj, tempExtraLines);
				closeFunc();
			}
		};
		const changeFunc = (e) => {
			tempExtraLines = e.currentTarget.value;
			isValid = (Number(tempExtraLines) > -1 && Number(tempExtraLines) < 6);
			if (isValid) {
				invalidMsg.setAttribute("hidden", "");
			} else {
				invalidMsg.removeAttribute("hidden", "");
			}
		};
		let invalidMsg = document.createElement("span");
		invalidMsg.appendChild(document.createTextNode("The value should be between 0 and 5 inclusively!"));
		invalidMsg.setAttribute("hidden", "");

		return html`
            <style>
                .title {
                    font-size: x-large;    
                    font-weight: bold;
                    margin-bottom: 20px;
                }
                .button-box {
                    display: flex;
                    margin-top: 20px;
                }
                .msg {
                    color: red;
                }
                .msg[hidden] {
                    visibility: hidden;
                }
            </style>

            <div class="title">Legend settings</div>    
            <div> 
                <text-box type="number" label="Extra lines on top [0 - 5]" 
                    @change="${changeFunc}"
                    .value="${tempExtraLines}"></text-box>
            </div>
            <div class="msg">${invalidMsg}</div>
            <div class="button-box">
                <mwc-button @click="${closeFunc}" label="Close"></mwc-button>
                <mwc-button @click="${applyFunc}" label="Apply"></mwc-button>
            </div>`;
	}

	applyNewSettings(obj, extraLines) {
		const currentSettings = obj.moduleState;
		let oldExtraLines = currentSettings.extraLines;
		if (oldExtraLines == extraLines) return;
        
		let lines = Number.parseInt(extraLines);
		this.addMetadata(obj, { ...obj.moduleState, extraLines: lines });
        
		// shift elements extraLines
		const stickedTopObjectIndexes = [0, 19, 20, 21];
		const diff = Number(oldExtraLines) * lineHeight - lines * lineHeight;
		for (let index = 0; index < obj._objects.length; index++) {
			if (stickedTopObjectIndexes.indexOf(index) === -1) {
				const item = obj.item(index);
				item.set('top', item.get('top') - diff);
			}
		}
		
		obj.set('top', obj.get('top') + diff);
        
		let tmpLine = new fabric.Line([0, 10, this.canvasSize.w, 10], { stroke: "black" });
		obj.add(tmpLine).removeWithUpdate(tmpLine);

		this.canvas.renderAll();
		this.canvas.trigger("object:modified", { target: obj });
	}
}

window.customElements.define("inspx-draw-legend", DrawLegendModule);
